import React from 'react';
import { FaStar, FaTrashAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { Dinosaur } from '../../types';
import { richTextToString } from '../../utils/richTextUtils';

import CreaturePlaceholder from './CreaturePlaceholder';

interface FavoriteCreatureCardProps {
  creature: Dinosaur;
  onRemove: (favoriteId: string, itemName: string) => void;
  favoriteId: string;
  removingId: string | null;
}

const FavoriteCreatureCard: React.FC<FavoriteCreatureCardProps> = ({
  creature,
  onRemove,
  favoriteId,
  removingId,
}) => {
  const {
    id,
    documentId,
    name_jp,
    name_en,
    scientificName,
    description_for_kids,
    image,
    classification,
    size,
    diet,
  } = creature;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200">
      <Link to={`/creatures/${documentId || id}`} className="block">
        <div className="relative">
          <div className="aspect-square bg-gray-100 rounded-t-lg flex items-center justify-center group-hover:bg-gray-50 transition-colors">
            {image ? (
              <img
                src={image}
                alt={name_jp}
                className="w-full h-full object-cover rounded-t-lg group-hover:scale-105 transition-transform duration-200"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CreaturePlaceholder size="sm" />
              </div>
            )}
          </div>

          {/* お気に入りアイコン */}
          <div className="absolute top-2 right-2">
            <FaStar className="text-yellow-500 text-xl drop-shadow-md" />
          </div>

          {/* 分類バッジ */}
          {classification && (
            <div className="absolute bottom-2 left-2">
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                {classification}
              </span>
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-amber-700 transition-colors text-lg">
            {name_jp}
          </h3>

          {name_en && (
            <p className="text-sm text-gray-600 italic mb-2 group-hover:text-amber-600 transition-colors">
              {name_en}
            </p>
          )}

          {scientificName && (
            <p className="text-xs text-gray-500 italic mb-2 group-hover:text-gray-600 transition-colors">
              {scientificName}
            </p>
          )}

          {description_for_kids && (
            <p className="text-xs text-gray-500 line-clamp-2 group-hover:text-gray-600 transition-colors mb-2">
              {richTextToString(description_for_kids)}
            </p>
          )}

          <div className="flex flex-wrap gap-2 text-xs mb-4">
            {size && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">サイズ: {size}</span>
            )}
            {diet && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded">食性: {diet}</span>
            )}
          </div>

          {/* 詳細を見るボタン */}
          <div className="inline-block bg-green-600 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-green-700 transition-colors">
            詳細を見る
          </div>
        </div>
      </Link>

      {/* お気に入り削除ボタン */}
      <div className="px-4 pb-4">
        <button
          onClick={() => onRemove(favoriteId, name_jp)}
          disabled={removingId === favoriteId}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {removingId === favoriteId ? (
            <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
          ) : (
            <FaTrashAlt className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {removingId === favoriteId ? '削除中...' : 'お気に入りから削除'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default FavoriteCreatureCard;
